import {
  Button,
  ContentWrapper,
  Loader,
  ScrollButton,
  SearchInput,
  ZoomInput,
} from 'components';
import { EditEventType } from 'constants/sidebars';
import moment from 'moment';
import {
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from 'pages/UserProfileCommunities/components/CommunitiesList/styled';
import React, { FC, MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import {
  getCommunities,
  getCommunitiesLoading,
} from 'store/selectors/communities';
import { getMyEventsLoaders } from 'store/selectors/events';
import { breakpoints } from 'theme';
import {
  EventReply,
  EventResponse,
  FullUser,
  MyAppointmentsEvent,
  MyEventsStateLoaders,
  MyEventStateData,
} from 'types';

import EventCard from './EventCard';
import { ReplyFormData } from './form';
import { MyEventsFilterValues, ScrollButtonsStatus } from './index';
import MyAppointmentFilters from './MyAppointmentFilters';
import {
  Heading,
  Layout,
  NoEventsPlaceholder,
  StyledFilterBar,
  StyledFloatingLoader,
  TasksList,
} from './styled';

interface UserProfileSettingsPageProps {
  data: MyEventStateData;
  loaders: MyEventsStateLoaders;
  listRef: MutableRefObject<HTMLDivElement>;
  currentUser: FullUser;
  zoom: number;
  filterValues: MyEventsFilterValues;
  search: string;
  scrollButtonsStatus: ScrollButtonsStatus;
  handleScroll: (position: 'start' | 'end') => void;
  setFilterValues: (value: MyEventsFilterValues) => void;
  onZoomChange: (value: number) => void;
  setSearch: (value: string) => void;
  onReset: () => void;
  onSubmit: (
    type: EditEventType,
    data: ReplyFormData,
    event: MyAppointmentsEvent,
    reply: {
      reply: EventReply & { tasks: string[] };
      communityResponse: EventResponse;
    },
  ) => void;
  onEventHeaderClick: (event: MyAppointmentsEvent) => void;
  onEventClick: (event: MyAppointmentsEvent) => void;
  shouldRedirect?: boolean;
  setIsClearingSearch: (value: boolean) => void;
}

const UserProfileSettingsPage: FC<UserProfileSettingsPageProps> = ({
  data,
  loaders,
  listRef,
  currentUser,
  zoom,
  filterValues,
  search,
  scrollButtonsStatus,
  handleScroll,
  setFilterValues,
  onZoomChange,
  setSearch,
  onReset,
  onSubmit,
  onEventHeaderClick,
  onEventClick,
  shouldRedirect,
  setIsClearingSearch,
}) => {
  const { t } = useTranslation();
  const communities = useSelector(getCommunities);
  const areCommunitiesLoading = useSelector(getCommunitiesLoading);
  const isDownMd = useMedia(breakpoints.downMd);
  const isDownSmPlus = useMedia(breakpoints.downSmPlus);
  const loadersState = useSelector(getMyEventsLoaders);

  const [shouldResetLocalFilters, setShouldResetLocalFilters] =
    React.useState<boolean>();
  const [isClearingSearchLocal, setIsClearingSearchLocal] =
    React.useState<boolean>(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isClearingSearchRef = useRef<boolean>(false);
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);
  const [showLoaderImmediately, setShowLoaderImmediately] =
    React.useState(false);
  const [isButtonClicked, setIsButtonClicked] = React.useState(false);

  const eventTypesOptions: { [key: string]: string }[] = data.eventTypes && [
    { label: t('common.all'), value: 'all' },
    ...data.eventTypes.map((type) => {
      const community = communities.find((c) => c.id === type.communityId);

      return {
        label: type.title,
        value: type.id,
        icon: community?.logo || community?.name[0],
      };
    }),
  ];

  const eventsListComponent: JSX.Element | JSX.Element[] = useMemo(() => {
    if (isButtonClicked) {
      return <Loader />;
    }
    if (showLoaderImmediately) {
      return <Loader />;
    }
    if (isClearingSearchRef.current) {
      return <Loader />;
    }

    if (
      !data.events ||
      areCommunitiesLoading ||
      loadersState.mainLoader ||
      loaders.events ||
      isClearingSearchLocal
    ) {
      return <Loader />;
    } else if (data.events.length > 0) {
      return data.events?.map((e) => (
        <EventCard
          search={search}
          key={e.id}
          event={e}
          community={communities.find(
            (c) =>
              c.id === e.communityId ||
              c?.groups?.some((g) => g.id === e.groupId),
          )}
          currentUser={currentUser}
          zoom={zoom}
          tasks={data.tasks}
          isLoading={loaders.event === e.id}
          eventResponses={data.eventResponses}
          eventTypes={data.eventTypes}
          onSubmit={onSubmit}
          onEventHeaderClick={onEventHeaderClick}
          onEventClick={onEventClick}
        />
      ));
    }

    if (isClearingSearchLocal || isClearingSearchRef.current) {
      return <Loader />;
    }

    const isActivelySearching = search && search.trim().length > 0;
    if (isActivelySearching) {
      return (
        <NoEventsPlaceholder>
          <EmptyStateIcon>🔍</EmptyStateIcon>
          <EmptyStateTitle>
            {t('communitySettings.noSearchResults.title', 'No Search Results')}
          </EmptyStateTitle>
          <EmptyStateDescription>
            {t('communitySettings.noSearchResults.description', {
              searchResult: `"${search.trim()}"`,
            }).replace(/&quot;/g, '"')}
            <NoEventsPlaceholder.ButtonWrapper>
              <Button
                variant="default"
                width="100%"
                onClick={() => {
                  setIsClearingSearch(true);
                  setIsClearingSearchLocal(true);
                  setIsButtonClicked(true);
                  setShowLoaderImmediately(true);
                  isClearingSearchRef.current = true;
                  forceUpdate();
                  setSearch('');
                  if (searchInputRef.current) {
                    const input = searchInputRef.current.querySelector('input');
                    if (input) {
                      input.value = '';
                      input.dispatchEvent(
                        new Event('input', { bubbles: true }),
                      );
                    }
                  }
                }}
                disabled={isClearingSearchLocal || isClearingSearchRef.current}
              >
                {isClearingSearchLocal || isClearingSearchRef.current
                  ? t('common.loading', 'Loading...')
                  : t('communitySettings.clearSearch', 'Clear Search')}
              </Button>
            </NoEventsPlaceholder.ButtonWrapper>
          </EmptyStateDescription>
        </NoEventsPlaceholder>
      );
    }

    return (
      <NoEventsPlaceholder>
        <EmptyStateIcon>📅</EmptyStateIcon>
        <EmptyStateTitle>{t('event.noEvents.title')}</EmptyStateTitle>
        <EmptyStateDescription>
          {t('event.noEvents.description')}
        </EmptyStateDescription>
      </NoEventsPlaceholder>
    );
  }, [
    data,
    areCommunitiesLoading,
    communities,
    currentUser,
    zoom,
    loaders,
    loadersState.mainLoader,
    search,
    setSearch,
    t,
    isClearingSearchLocal,
    showLoaderImmediately,
    isButtonClicked,
  ]);

  useEffect(() => {
    if (data?.events?.length === 0 && filterValues.awaiting && shouldRedirect) {
      setFilterValues({
        ...filterValues,
        awaiting: false,
        from: moment().valueOf(),
        eventType: { label: 'All', value: 'all' },
      });
      setShouldResetLocalFilters((prevValue) => !prevValue);
    }
  }, [data]);

  useEffect(() => {
    if (isClearingSearchLocal && !loaders.events && !loadersState.mainLoader) {
      setIsClearingSearchLocal(false);
      isClearingSearchRef.current = false;
      setShowLoaderImmediately(false);
      setIsButtonClicked(false);
      setIsClearingSearch(false);
    }
  }, [
    isClearingSearchLocal,
    loaders.events,
    loadersState.mainLoader,
    setIsClearingSearch,
  ]);

  return (
    <>
      {data.events &&
        loaders.event &&
        !loaders.events &&
        !loadersState.mainLoader &&
        !areCommunitiesLoading && (
          <StyledFloatingLoader>
            <Loader size="60px" type={null} />
          </StyledFloatingLoader>
        )}
      <ContentWrapper>
        <Layout>
          <Layout.Header>
            {!isDownSmPlus && <Heading>{t('profile.myAppointments')}</Heading>}
            <StyledFilterBar>
              <MyAppointmentFilters
                filters={filterValues}
                eventTypesOptions={eventTypesOptions}
                noEndTime={true}
                setFilters={setFilterValues}
                resetFilters={onReset}
                applyFilters={setFilterValues}
                shouldResetLocalFilters={shouldResetLocalFilters}
              />
              <div ref={searchInputRef}>
                <SearchInput
                  value={search}
                  onChange={({ target }) => setSearch(target.value)}
                  fillWidth
                  noEndTime={true}
                  applyFilters={() => {}}
                />
              </div>
              <ZoomInput
                value={zoom}
                color="black"
                onChange={(zoom) => onZoomChange(zoom)}
              />
            </StyledFilterBar>
          </Layout.Header>
          <Layout.Data>
            {!isDownMd &&
              scrollButtonsStatus.right &&
              data.events &&
              data.events.length > 0 && (
                <ScrollButton
                  position="right"
                  isMenuOpen={true}
                  disabled={scrollButtonsStatus.disabled}
                  onClick={() => handleScroll('end')}
                />
              )}
            {!isDownMd &&
              scrollButtonsStatus.left &&
              data.events &&
              data.events.length > 0 && (
                <ScrollButton
                  position="left"
                  isMenuOpen={true}
                  disabled={scrollButtonsStatus.disabled}
                  onClick={() => handleScroll('start')}
                />
              )}
            <TasksList ref={listRef}>{eventsListComponent}</TasksList>
          </Layout.Data>
        </Layout>
      </ContentWrapper>
    </>
  );
};

export default UserProfileSettingsPage;
